import { useEffect, useMemo, useRef, useState } from 'react'

import { CustomPeriodFilter, PeriodFilter } from 'types/GlobalWidget'
import { DashboardFilter } from 'types/GlobalDashboardFilter'
import { TMetricQueryParams } from 'types/GlobalMetric'

import { useAiColumnIds } from 'redux/hooks/Entities'
import { useGlobalFilterForApi } from 'redux/hooks/GlobalFilter'
import { useTranslation } from 'language/translator'

import { arrayToObject, resolvePeriodFields } from 'utils/functions'
import { Filters, Sort } from 'utils/types'
import { getParsedData } from 'utils/underlyingData'

import { exportUnderlyingData, fetchUnderlyingData } from './api'
import { FilterSegmentParams } from './ChartView/ChartElement'
import { getActiveView } from './sourceView'
import { TDataDialogColumn, TDataDialogSource } from './types'
import {
  getSearchedParsedData,
  getSortedParsedData,
  INITIAL_SORT,
  getServerSearch,
  getServerSearchKey,
  getServerSort,
  getServerSortKey,
  isInitialSort,
  pruneColumnSearch,
  TDataDialogColumns,
  TRowsStatus,
  TServerRefresh,
  TDataDialogDataCache
} from './utils'
import {
  buildQueryParams,
  deriveColumnSelection,
  mergeFilters,
  removeLocalFilter,
  toVisibleColumnIds,
  upsertLocalFilter
} from './query'

// How long typing pauses before a search goes to the server.
const SERVER_SEARCH_DELAY_MS = 400

interface UseMetricDataArgs {
  source: TDataDialogSource | null
  // Baseline filters resolved by the caller (metric query filters, or dashboard
  // filters for widgets). Segment then local filters override these.
  baseFilters: TMetricQueryParams['filter']
  // Baseline period resolved by the caller, excluding the segment override.
  basePeriod: CustomPeriodFilter | PeriodFilter | null
  segmentFilters: Filters
  segmentPeriod: CustomPeriodFilter | null
  open: boolean
}

// The server search and sort of a fetch in flight, for one view.
type TPendingFetch = { key: string; search: string; sort: string }

export const useMetricData = (args: UseMetricDataArgs) => {
  const {
    source,
    baseFilters,
    basePeriod,
    segmentFilters,
    segmentPeriod,
    open
  } = args

  const { t } = useTranslation()
  const globalFilter = useGlobalFilterForApi()

  const [selectedViewId, setSelectedViewId] = useState<string | null>(null)
  const [status, setStatus] = useState<TRowsStatus>('loading')
  const [data, setData] = useState<TDataDialogDataCache>({})
  const [selectedColumns, setSelectedColumns] = useState<TDataDialogColumns>({})
  // Added via "add to filters" inside the dialog.
  const [localFilters, setLocalFilters] = useState<DashboardFilter>([])

  const requestIdRef = useRef(0)
  const pendingFetchRef = useRef<TPendingFetch | null>(null)
  const skipNextLocalFiltersFetchRef = useRef(true)

  const activeView = getActiveView(source, selectedViewId)
  const currentKey = activeView?.id ?? ''
  const aiColumnIds = useAiColumnIds(activeView?.entityId ?? null)

  const merged = useMemo(
    () => mergeFilters(baseFilters, segmentFilters, localFilters),
    [baseFilters, segmentFilters, localFilters]
  )
  const mergedFilters = merged.filters
  const filterLabels = merged.labels

  const periodFields = useMemo(
    () => resolvePeriodFields(segmentPeriod ?? basePeriod),
    [segmentPeriod, basePeriod]
  )

  // A refresh fetches without replacing the table by a loading state, for a
  // search typed into it or a sort chosen in it. Edited columns are the new
  // selection; otherwise the view's current selection is fetched.
  const fetchData = (
    refresh?: TServerRefresh,
    editedColumns?: TDataDialogColumn[]
  ) => {
    if (!activeView) return

    const key = activeView.id
    const current = data[key]
    const selection = editedColumns ?? Object.values(selectedColumns[key] ?? {})

    const queryParams = buildQueryParams(mergedFilters, periodFields)
    const entityColumns =
      selection.length > 0 ? toVisibleColumnIds(selection) : undefined

    // A capped table holds only the first rows, so its search and sort run
    // on the server to reach the rest. Edited columns drop the searches on
    // the columns they remove, like the table does once the rows arrive.
    const columnSearch = current?.capped
      ? getServerSearch(
        editedColumns && entityColumns
          ? pruneColumnSearch(current.search, entityColumns)
          : current.search
      )
      : undefined
    const rowSort = current?.capped
      ? getServerSort(current.sort, current.raw)
      : undefined

    const requestId = ++requestIdRef.current
    const pending = {
      key,
      search: getServerSearchKey(columnSearch),
      sort: getServerSortKey(rowSort)
    }

    pendingFetchRef.current = pending
    setStatus(refresh ?? 'loading')

    fetchUnderlyingData(activeView, queryParams, globalFilter, {
      entityColumns,
      columnSearch,
      rowSort
    })
      .then((response) => {
        if (requestIdRef.current !== requestId) return

        pendingFetchRef.current = null
        setStatus('ready')
        const raw = response.data.data

        setData((prev) => {
          let newSort = prev[key]?.sort ?? INITIAL_SORT
          let newSearch = prev[key]?.search ?? {}

          if (editedColumns && entityColumns) {
            if (!entityColumns.includes(newSort.attribute)) {
              newSort = INITIAL_SORT
            }

            newSearch = pruneColumnSearch(newSearch, entityColumns)
          }

          return {
            ...prev,
            [key]: {
              raw,
              parsed: getParsedData(raw, aiColumnIds, t),
              search: newSearch,
              sort: newSort,
              rowsSort: newSort,
              serverSearchKey: pending.search,
              serverSortKey: pending.sort,
              // a searched response says nothing about the whole selection
              capped: columnSearch
                ? (prev[key]?.capped ?? false)
                : Boolean(raw.has_more)
            }
          }
        })

        if (Object.keys(selectedColumns[key] || {}).length === 0) {
          setSelectedColumns((prev) => ({
            ...prev,
            [key]: deriveColumnSelection(raw)
          }))
        }
      })
      .catch(() => {
        if (requestIdRef.current !== requestId) return

        pendingFetchRef.current = null
        // a table on screen stays, with its rows from before
        setStatus(refresh ? 'refresh_failed' : 'failed')
      })
  }

  // Fetch whenever the active view changes (including on open). Always fetch
  // rather than serving the cache: local filters only refetch the current
  // view, so a cached sibling view could be stale.
  useEffect(() => {
    if (open && activeView) {
      fetchData()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, activeView?.id])

  // Refetch when local filters change (skip the initial mount).
  useEffect(() => {
    if (skipNextLocalFiltersFetchRef.current) {
      skipNextLocalFiltersFetchRef.current = false

      return
    }

    if (open && activeView) {
      fetchData()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localFilters])

  const entry = currentKey ? data[currentKey] : undefined
  const searchKey = getServerSearchKey(getServerSearch(entry?.search ?? {}))
  // only a sort the server runs needs a fetch; others sort the fetched rows
  const sortKey = getServerSortKey(
    entry ? getServerSort(entry.sort, entry.raw) : undefined
  )

  // A capped table searches on the server once typing pauses and sorts on
  // the server right away, whenever its rows were fetched, or are being
  // fetched, with another search or sort. That includes a table that just
  // became capped.
  useEffect(() => {
    if (!open || !entry?.capped) {
      return undefined
    }

    const pending = pendingFetchRef.current
    const fetchedWith =
      pending?.key === currentKey
        ? pending
        : { search: entry.serverSearchKey, sort: entry.serverSortKey }

    const refresh: TServerRefresh | null =
      fetchedWith.search !== searchKey
        ? 'search'
        : fetchedWith.sort !== sortKey
          ? 'sort'
          : null

    if (!refresh) {
      return undefined
    }

    const timeout = setTimeout(
      () => fetchData(refresh),
      refresh === 'search' ? SERVER_SEARCH_DELAY_MS : 0
    )

    return () => clearTimeout(timeout)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    open,
    currentKey,
    searchKey,
    sortKey,
    entry?.capped,
    entry?.serverSearchKey,
    entry?.serverSortKey
  ])

  // Sorting is the expensive pass, so it is memoized on its own inputs. A
  // keystroke only changes `search`, and then only the filter re-runs.
  const sortedData = useMemo(
    () => getSortedParsedData(entry),
    [entry?.parsed, entry?.rowsSort, entry?.capped, entry?.serverSortKey]
  )

  const parsedData = useMemo(
    () => getSearchedParsedData(sortedData, entry?.search, entry?.sort),
    [sortedData, entry?.search, entry?.sort]
  )

  const handleSort = (sort: Sort) => {
    if (!currentKey) return

    setData((prev) => {
      const current = prev[currentKey]

      if (!current) return prev

      // A capped table that needs other rows for the sort keeps showing the
      // rows it has, in their order, until the server's rows arrive.
      const waitsForServer =
        current.capped &&
        getServerSortKey(getServerSort(sort, current.raw)) !==
          current.serverSortKey

      return {
        ...prev,
        [currentKey]: {
          ...current,
          sort,
          rowsSort: waitsForServer ? current.rowsSort : sort
        }
      }
    })
  }

  // Set (non-empty) or clear (empty) the free-text search on one column.
  const handleSearch = (columnId: string, query: string) => {
    if (!currentKey) return

    setData((prev) => {
      const entry = prev[currentKey]

      if (!entry) return prev

      const search = { ...entry.search }

      if (query.trim().length > 0) {
        search[columnId] = query
      } else {
        delete search[columnId]
      }

      return { ...prev, [currentKey]: { ...entry, search } }
    })
  }

  const handleEditColumns = (value: TDataDialogColumn[]) => {
    if (!currentKey) return

    fetchData(undefined, value)
    setSelectedColumns((prev) => ({
      ...prev,
      [currentKey]: arrayToObject(value)
    }))
  }

  const handleAddLocalFilter = (value: FilterSegmentParams) => {
    setLocalFilters((prev) => upsertLocalFilter(prev, value))
  }

  const handleDeleteLocalFilter = (params: { id: string; value: string }) => {
    setLocalFilters((prev) => removeLocalFilter(prev, params))
  }

  // Exports every row of the current view on the server, with the table's
  // columns, searches and sort.
  const exportAllRows = (fileName: string) => {
    if (!activeView || !entry) {
      return Promise.reject(new Error('There are no rows to export'))
    }

    const selection = Object.values(selectedColumns[currentKey] ?? {})

    return exportUnderlyingData(
      activeView,
      buildQueryParams(mergedFilters, periodFields),
      globalFilter,
      {
        entityColumns:
          selection.length > 0 ? toVisibleColumnIds(selection) : undefined,
        columnSearch: getServerSearch(entry.search),
        rowSort: isInitialSort(entry.sort)
          ? undefined
          : { column: entry.sort.attribute, direction: entry.sort.direction }
      },
      fileName
    )
  }

  const reset = () => {
    setData({})
    setStatus('loading')
    setSelectedColumns({})
    setLocalFilters([])
    setSelectedViewId(null)
    // a fetch still in flight belongs to the closed dialog
    requestIdRef.current += 1
    pendingFetchRef.current = null
    skipNextLocalFiltersFetchRef.current = true
  }

  return {
    activeView,
    columns: (currentKey && selectedColumns[currentKey]) || {},
    exportAllRows,
    fetched: status !== 'loading',
    filterLabels,
    // rows follow the fetched ones in the current result
    hasMore: Boolean(entry?.raw.has_more),
    localFilters,
    mergedFilters,
    onAddLocalFilter: handleAddLocalFilter,
    onChangeView: setSelectedViewId,
    onDeleteLocalFilter: handleDeleteLocalFilter,
    onEditColumns: handleEditColumns,
    onSearch: handleSearch,
    onSort: handleSort,
    parsedData,
    periodFields,
    rawData: entry?.raw ?? null,
    reset,
    // the fetched rows are the first ones in the table's sort over all rows
    sortedOnServer: Boolean(entry?.raw.sorted),
    status
  }
}
